/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Side of the target the tour card sits on. */
export type Side = 'top' | 'bottom' | 'left' | 'right';

export interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

/** Card anchored beside its target; `side` is null when it had to sit over a very large target. */
export interface PopoverLayout {
  kind: 'popover';
  top: number;
  left: number;
  side: Side | null;
  /** Card width: the natural width, or narrower when that is what fits beside the target. */
  width: number;
  /** Arrow position along the card edge that faces the target, in px from that edge's start. */
  arrow: number;
}

/** Card docked to the bottom (or top) edge on phones and short screens. */
export interface SheetLayout {
  kind: 'sheet';
  dock: 'top' | 'bottom';
}

export type CardLayout = PopoverLayout | SheetLayout;

/** Minimum distance between the card and the viewport edge. */
export const EDGE = 12;
/** Distance between the spotlight and the card; leaves room for the arrow. */
export const GAP = 12;
/** Space the spotlight ring keeps around its target. */
export const PAD = 6;
/** The popover's natural width (CSS: `min(360px, 100vw - 24px)`). */
export const CARD_WIDTH = 360;
/** Beside a target the card may narrow to this rather than give up the side. */
export const MIN_CARD_WIDTH = 280;
/** Keeps the arrow clear of the card's rounded corners. */
const ARROW_INSET = 20;
/** The top bar: scrolling a target above this would tuck it under the header. */
const TOP_CLEARANCE = 64;

const OPPOSITE: Record<Side, Side> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

/** Phones (under 600px wide) always get the sheet. */
export function isPhone(width: number): boolean {
  return width < 600;
}

/** Short screens (560px tall or less) use the sheet whenever the popover has no side to sit on. */
const SHORT = 560;

/** The target grown by the spotlight padding, then cut to the part that is inside the viewport. */
export function spotlightBox(rect: Box, vw: number, vh: number): Box {
  const top = Math.max(rect.top - PAD, 0);
  const left = Math.max(rect.left - PAD, 0);
  const bottom = Math.min(rect.top + rect.height + PAD, vh);
  const right = Math.min(rect.left + rect.width + PAD, vw);
  return { top, left, width: Math.max(right - left, 0), height: Math.max(bottom - top, 0) };
}

/**
 * Places the card beside the spotlight: the preferred side, else its opposite, else either of the
 * other two - the first with room for the whole card (beside it, the card may narrow down to
 * {@link MIN_CARD_WIDTH}). It is clamped inside the viewport and never overlaps the spotlight. A
 * target so large that no side has room gets the card inside its bottom corner, without an arrow,
 * rather than off-screen.
 */
export function placePopover(
  spot: Box,
  card: Size,
  vw: number,
  vh: number,
  prefer: Side,
): PopoverLayout {
  const bottom = spot.top + spot.height;
  const right = spot.left + spot.width;
  const space: Record<Side, number> = {
    top: spot.top - GAP - EDGE,
    bottom: vh - bottom - GAP - EDGE,
    left: spot.left - GAP - EDGE,
    right: vw - right - GAP - EDGE,
  };
  const minWidth = Math.min(card.width, MIN_CARD_WIDTH);
  const room: Record<Side, boolean> = {
    top: space.top >= card.height,
    bottom: space.bottom >= card.height,
    left: space.left >= minWidth,
    right: space.right >= minWidth,
  };
  const vertical = prefer === 'top' || prefer === 'bottom';
  const order: Side[] = vertical
    ? [prefer, OPPOSITE[prefer], 'right', 'left']
    : [prefer, OPPOSITE[prefer], 'bottom', 'top'];
  const side = order.find((s) => room[s]);

  const maxLeft = vw - card.width - EDGE;
  const maxTop = vh - card.height - EDGE;
  const cx = spot.left + spot.width / 2;
  const cy = spot.top + spot.height / 2;

  if (side === 'top' || side === 'bottom') {
    const left = clamp(cx - card.width / 2, EDGE, maxLeft);
    return {
      kind: 'popover',
      side,
      left,
      top: side === 'bottom' ? bottom + GAP : spot.top - GAP - card.height,
      width: card.width,
      arrow: clamp(cx - left, ARROW_INSET, card.width - ARROW_INSET),
    };
  }
  if (side) {
    const top = clamp(cy - card.height / 2, EDGE, maxTop);
    const width = Math.min(card.width, space[side]);
    return {
      kind: 'popover',
      side,
      top,
      left: side === 'right' ? right + GAP : spot.left - GAP - width,
      width,
      arrow: clamp(cy - top, ARROW_INSET, card.height - ARROW_INSET),
    };
  }
  return {
    kind: 'popover',
    side: null,
    top: clamp(bottom - card.height - EDGE, EDGE, maxTop),
    left: clamp(right - card.width - EDGE, EDGE, maxLeft),
    width: card.width,
    arrow: 0,
  };
}

/**
 * Popover or sheet. Phones get the sheet. A short screen that is wide enough (a landscape phone)
 * keeps the popover when there is a side of the target with room for it - so the card never sits
 * on its target - and docks the sheet otherwise. Everything else gets the popover.
 *
 * `popoverHeight` is the card's height as a popover, which differs from its height as a (wider)
 * sheet; deciding with it keeps the choice stable instead of flipping as the card re-measures.
 */
export function layoutCard(
  spot: Box,
  card: Size,
  popoverHeight: number,
  vw: number,
  vh: number,
  prefer: Side,
): CardLayout {
  if (!isPhone(vw)) {
    const popover = placePopover(
      spot,
      { width: card.width, height: popoverHeight },
      vw,
      vh,
      prefer,
    );
    if (popover.side || vh > SHORT) return popover;
  }
  return { kind: 'sheet', dock: sheetDock(spot, card.height, vh) };
}

/**
 * Which edge the sheet docks to: the bottom, unless that would hide the spotlight and the top
 * would not (the feedback button, pinned to the bottom corner). When both would cover part of a
 * tall target, the edge that hides less of it wins.
 */
export function sheetDock(spot: Box, sheetHeight: number, vh: number): 'top' | 'bottom' {
  const coveredByBottom = spot.top + spot.height - (vh - sheetHeight - GAP);
  if (coveredByBottom <= 0) return 'bottom';
  const coveredByTop = sheetHeight + GAP - spot.top;
  return coveredByTop < coveredByBottom ? 'top' : 'bottom';
}

/**
 * True when the element is really on screen: rendered, not hidden by CSS, with a size, and not
 * parked off to the side like the closed phone drawer. Below or above the fold still counts -
 * the tour scrolls it into view.
 */
export function isOnScreen(el: Element, uncovered = true): boolean {
  if (el.checkVisibility?.({ checkVisibilityCSS: true, checkOpacity: true }) === false)
    return false;
  const r = el.getBoundingClientRect();
  return (
    r.width > 0 &&
    r.height > 0 &&
    r.right > 0 &&
    r.left < window.innerWidth &&
    !(uncovered && isCovered(el, r))
  );
}

/**
 * Layers that do not hide a target: the tour's own, and menus or popovers, which are only up for a
 * moment (the Help menu the tour was started from is still closing when the tour looks around).
 */
const PASSING_LAYERS =
  '.tour-root, .tour-invite, .menu-pop, [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]';

/**
 * True when something else is painted over the element: the explorer's list drawer over the
 * diagram pane, the phone nav drawer's backdrop over the page. It is tested down the middle, at a
 * quarter, half and three quarters of its height, and counts as covered only when all three are,
 * so a sticky bar across part of it does not. Passing layers do not count, and a point outside the
 * viewport cannot be tested and counts as uncovered.
 */
function isCovered(el: Element, r: DOMRect): boolean {
  if (typeof document.elementsFromPoint !== 'function') return false;
  const x = Math.max(0, r.left + r.width / 2);
  return [0.25, 0.5, 0.75].every((at) => {
    const y = r.top + r.height * at;
    if (y < 0 || y >= window.innerHeight) return false;
    const top = document.elementsFromPoint(x, y).find((hit) => !hit.closest(PASSING_LAYERS));
    return !!top && !el.contains(top) && !top.contains(el);
  });
}

/**
 * First element matching `selector` that is on screen. `uncovered` also rules out elements that
 * something else is painted over; leave it off while following a target through a scroll, when a
 * sticky bar can pass over it for a moment.
 */
export function firstVisible(selector: string, uncovered = true): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(selector)) {
    if (isOnScreen(el, uncovered)) return el;
  }
  return null;
}

/** Fixed chrome (top bar, feedback button) does not move when its page scrolls. */
function isFixed(el: Element | null): boolean {
  for (let node = el; node; node = node.parentElement) {
    if (getComputedStyle(node).position === 'fixed') return true;
  }
  return false;
}

/** Nearest ancestor that actually scrolls vertically, else the document. */
function scrollParent(el: Element): Element {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  // Standards mode: the root element is the document's scroller.
  return document.documentElement;
}

/**
 * On a phone the sheet covers the bottom of the screen; if the target sits under it, scroll its
 * container just enough to lift it into the open area above - never so far that it slides under
 * the top bar. Fixed chrome cannot be scrolled; {@link sheetDock} moves the sheet instead.
 */
export function liftAboveSheet(el: Element, sheetHeight: number): void {
  if (isFixed(el)) return;
  const r = el.getBoundingClientRect();
  const hidden = r.bottom + PAD - (window.innerHeight - sheetHeight - GAP);
  const delta = Math.min(hidden, r.top - PAD - TOP_CLEARANCE);
  if (delta > 0) scrollParent(el).scrollTop += delta;
}
